import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, X } from "lucide-react";

const SIZES = ["S", "M", "L", "XL", "XXL"];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [collectionId, setCollectionId] = useState(params.get("collection") || "all");
  const [size, setSize] = useState(params.get("size") || "");
  const [inStockOnly, setInStockOnly] = useState(params.get("in_stock") === "1");
  const [minPrice, setMinPrice] = useState(params.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max_price") || "");
  const [sort, setSort] = useState(params.get("sort") || "");

  useEffect(() => {
    api.get("/collections").then(({ data }) => setCollections(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (collectionId && collectionId !== "all") p.set("collection_id", collectionId);
    if (size) p.set("size", size);
    if (inStockOnly) p.set("in_stock", "true");
    if (minPrice) p.set("min_price", minPrice);
    if (maxPrice) p.set("max_price", maxPrice);
    if (sort) p.set("sort", sort);
    api
      .get(`/products-v2?${p.toString()}`)
      .then(({ data }) => setProducts(data || []))
      .catch(() => setProducts([]));

    // Mirror to the URL (for share/back)
    const share = new URLSearchParams();
    if (q) share.set("q", q);
    if (collectionId && collectionId !== "all") share.set("collection", collectionId);
    if (size) share.set("size", size);
    if (inStockOnly) share.set("in_stock", "1");
    if (minPrice) share.set("min_price", minPrice);
    if (maxPrice) share.set("max_price", maxPrice);
    if (sort) share.set("sort", sort);
    setParams(share, { replace: true });
  }, [q, collectionId, size, inStockOnly, minPrice, maxPrice, sort, setParams]);

  const activeFilterCount = useMemo(
    () =>
      (collectionId !== "all" ? 1 : 0) +
      (size ? 1 : 0) +
      (inStockOnly ? 1 : 0) +
      (minPrice ? 1 : 0) +
      (maxPrice ? 1 : 0),
    [collectionId, size, inStockOnly, minPrice, maxPrice]
  );

  const clearFilters = () => {
    setCollectionId("all");
    setSize("");
    setInStockOnly(false);
    setMinPrice("");
    setMaxPrice("");
  };

  const count = products.length;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="shop-page">
      <div className="mb-12">
        <p className="label-xs opacity-60">Collection</p>
        <h1 className="mt-3 text-5xl lg:text-6xl font-serif tracking-tighter">Shop All</h1>
        <p className="mt-4 opacity-70 max-w-xl">
          Explore our curated capsule of oversized essentials. Every silhouette, every seam — considered.
        </p>
      </div>

      {/* Top controls: search + sort */}
      <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between border-y py-6 mb-8">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
          <Input
            placeholder="Search products…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 rounded-none border-foreground/20 focus-visible:ring-1"
            data-testid="shop-search"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44 rounded-none border-foreground/20" data-testid="sort-select">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="price_asc">Price: Low to High</SelectItem>
            <SelectItem value="price_desc">Price: High to Low</SelectItem>
            <SelectItem value="rating">Top Rated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-14">
        {/* Sidebar filters */}
        <aside className="space-y-8">
          <div className="flex items-center justify-between">
            <p className="label-xs">Filters {activeFilterCount ? `(${activeFilterCount})` : ""}</p>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs opacity-70 hover:opacity-100 link-underline"
                data-testid="clear-filters-btn"
              >
                Clear
              </button>
            )}
          </div>

          {/* Collection */}
          <div>
            <p className="label-xs opacity-60 mb-3">Collection</p>
            <div className="space-y-2">
              <button
                onClick={() => setCollectionId("all")}
                data-testid="filter-collection-all"
                className={`block w-full text-left text-sm py-1 ${
                  collectionId === "all" ? "font-semibold" : "opacity-70 hover:opacity-100"
                }`}
              >
                All Collections
              </button>
              {collections.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCollectionId(c.id)}
                  data-testid={`filter-collection-${c.slug}`}
                  className={`block w-full text-left text-sm py-1 ${
                    collectionId === c.id ? "font-semibold" : "opacity-70 hover:opacity-100"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Size */}
          <div>
            <p className="label-xs opacity-60 mb-3">Size</p>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(size === s ? "" : s)}
                  data-testid={`filter-size-${s}`}
                  className={`w-10 h-10 border label-xs transition-colors ${
                    size === s
                      ? "bg-foreground text-background border-foreground"
                      : "border-foreground/20 hover:border-foreground"
                  }`}
                  style={{ transitionProperty: "background-color, color, border-color" }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Price */}
          <div>
            <p className="label-xs opacity-60 mb-3">Price (₹)</p>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Min"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                className="rounded-none border-foreground/20"
                data-testid="filter-price-min"
              />
              <Input
                type="number"
                placeholder="Max"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="rounded-none border-foreground/20"
                data-testid="filter-price-max"
              />
            </div>
          </div>

          {/* Availability */}
          <div>
            <p className="label-xs opacity-60 mb-3">Availability</p>
            <label
              className="flex items-center gap-3 text-sm cursor-pointer select-none"
              data-testid="filter-in-stock"
            >
              <input
                type="checkbox"
                checked={inStockOnly}
                onChange={(e) => setInStockOnly(e.target.checked)}
                className="h-4 w-4 accent-foreground"
              />
              <span>In stock only</span>
            </label>
          </div>
        </aside>

        {/* Product grid */}
        <div>
          <p className="label-xs opacity-60 mb-6" data-testid="results-count">
            {count} PRODUCT{count === 1 ? "" : "S"}
          </p>
          {products.length === 0 ? (
            <div className="text-center py-24 border border-dashed">
              <p className="opacity-60">No products match your filters.</p>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="mt-4 inline-flex items-center gap-2 text-sm link-underline"
                >
                  <X className="h-3 w-3" /> Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 lg:gap-8">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
